import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { money, currentPeriod, today } from '../lib/format'
import { Panel, Alert, Loading, Empty, DocHead } from '../components/ui'
import { MonthPicker } from '../components/MonthRangePicker'

interface SummaryRow {
  id: number
  client_name: string
  contract_code: string | null
  work_order_code: string
  dispatch_type: string
  writer_name: string
  owner_name: string | null
  job_type_name: string | null
  qty: number
  unit_price: number
  amount: number
}

export default function Summary() {
  const [period, setPeriod] = useState(currentPeriod())
  const [data, setData] = useState<{ rows: SummaryRow[]; total_qty: number; total_amount: number } | null>(null)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')

  useEffect(() => {
    setData(null)
    setMsg('')
    api.get<any>(`/work-orders/summary?period=${period}`).then(setData).catch((e) => setError(e.message))
  }, [period])

  const sync = async (mode: 'preview' | 'execute') => {
    setError(''); setMsg('')
    try {
      const res = await api.post<any>(`/sync/${mode}`, { target: 'summary', period })
      if (mode === 'preview') {
        setMsg(
          `預覽：${res.row_count} 列，範圍 ${res.range}，將變動 ${res.changes.length} 個儲存格` +
          (res.truncated_warning ? `\n⚠ ${res.truncated_warning}` : '')
        )
      } else {
        setMsg(`已寫入 Google Sheet：${res.range}，共 ${res.updatedCells} 個儲存格`)
      }
    } catch (e: any) {
      setError(e.message)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>3. 外包申請匯總表</h2>
        <span className="hint">申請月份</span>
        <MonthPicker value={period} onChange={setPeriod} />
        <div className="spacer" />
        <div className="btn-row">
          <button onClick={() => sync('preview')}>同步預覽</button>
          <button className="primary" onClick={() => sync('execute')}>寫入 Google Sheet</button>
          <button onClick={() => window.print()}>列印</button>
        </div>
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok" style={{ whiteSpace: 'pre-wrap' }}>{msg}</div>}

      {!data ? (
        <Loading />
      ) : data.rows.length === 0 ? (
        <Panel><Empty>這個月還沒有發包明細</Empty></Panel>
      ) : (
        <div className="print-doc">
          {/* 申請日期取列印當天（台北時區），不是資料所屬的月份 */}
          <DocHead
            lead="文案案件"
            title="發包申請"
            sub={`申請日期：${today().replace(/-/g, ' / ')}`}
            subAlign="right"
          />

          <table>
            <thead>
              <tr>
                <th style={{ width: 34 }}>序</th>
                <th>客戶</th>
                <th>合約編號</th>
                <th>發包單號</th>
                {/* 34px 會把「類型」擠成兩行 */}
                <th style={{ width: 44, whiteSpace: 'nowrap' }}>類型</th>
                <th>承包人</th>
                <th>負責人</th>
                <th>案件類型</th>
                <th className="num">篇數</th>
                <th className="num">單價</th>
                <th className="num">金額</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td>{r.client_name}</td>
                  <td>{r.contract_code}</td>
                  <td>{r.work_order_code}</td>
                  {/* 列印版面窄，只印一個字：外／內 */}
                  <td>{r.dispatch_type === '內包' ? '內' : '外'}</td>
                  <td>{r.writer_name}</td>
                  <td>{r.owner_name}</td>
                  <td>{r.job_type_name}</td>
                  <td className="num">{r.qty}</td>
                  <td className="num">{money(r.unit_price)}</td>
                  <td className="num">{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* 合計列整列灰底、數字加粗，上方粗線、下方雙線，比照財報的結帳列 */}
              <tr>
                <th colSpan={7}></th>
                <th>總篇數</th>
                <th className="num">{data.total_qty}</th>
                <th>總額</th>
                <th className="num">{money(data.total_amount)}</th>
              </tr>
            </tfoot>
          </table>

          {/* 簽核區與發包單、驗收單同一版式：灰底標題列標職稱，下方整格留白給簽名 */}
          <h2 style={{ marginTop: 28 }}>簽核</h2>
          <table className="sign-table">
            <tbody>
              <tr>
                {/* 由經辦往上簽：發包人 → 直屬主管 → 部門主管 */}
                <th style={{ width: '34%' }}>發包人</th>
                <th style={{ width: '33%' }}>直屬主管</th>
                <th>部門主管</th>
              </tr>
            </tbody>
            <tbody className="sign-row">
              <tr><td></td><td></td><td></td></tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
